import { BudgetLine, ManualMatchMap, budgetLineKey, DEFAULT_BUDGET_LINES } from '../data/budgetData';

export interface GLRow {
  month: string;
  accCode: string;
  accName: string;
  desc: string;        // row[3] — Keterangan in GL GAB
  branch: string;
  area: string;
  debit: number;
  credit: number;
  amount: number;
  rowIndex: number;    // original index in glData.data, used as manual match key
}

export type MatchStatus = 'matched' | 'manual' | 'unmatched';

export interface MatchedActual {
  budgetKey: string;
  actual: number;
  status: MatchStatus;
  matchedRows: GLRow[];
}

export function parseGLRows(rawData: any[][]): GLRow[] {
  return rawData.map((row, i) => ({
    month: String(row[0] ?? ''),
    accCode: String(row[1] ?? ''),
    accName: String(row[2] ?? ''),
    desc: String(row[3] ?? ''),
    branch: String(row[4] ?? ''),
    area: String(row[5] ?? ''),
    debit: Number(row[6]) || 0,
    credit: Number(row[7]) || 0,
    amount: Number(row[8]) || 0,
    rowIndex: row[9] !== undefined ? Number(row[9]) : i,
  }));
}

export function filterByPeriod(rows: GLRow[], months: string[]): GLRow[] {
  if (!months.length) return rows;
  const set = new Set(months);
  return rows.filter(r => set.has(r.month));
}

export function filterByBranch(rows: GLRow[], branch: string): GLRow[] {
  if (branch === 'ALL') return rows;
  return rows.filter(r => r.branch === branch);
}

export function filterBySingleMonth(rows: GLRow[], month: string): GLRow[] {
  if (month === 'ALL') return rows;
  return rows.filter(r => r.month === month);
}

// Helper to retrieve the corresponding default budget line for a given line to prevent import mismatches
export function getDefaultLine(line: BudgetLine, allLines: BudgetLine[]): BudgetLine | null {
  // 1. Exact match by noAcc and keterangan
  const exact = DEFAULT_BUDGET_LINES.find(
    d => d.noAcc === line.noAcc && d.keterangan.toLowerCase() === line.keterangan.toLowerCase()
  );
  if (exact) return exact;

  // 2. Index-based match within same noAcc
  const sameAccInCurrent = allLines.filter(l => l.noAcc === line.noAcc);
  const idx = sameAccInCurrent.indexOf(line);
  if (idx !== -1) {
    const sameAccInDefault = DEFAULT_BUDGET_LINES.filter(d => d.noAcc === line.noAcc);
    if (sameAccInDefault[idx]) {
      return sameAccInDefault[idx];
    }
  }
  return null;
}

// Helper to extract clean lowercase keywords by splitting on delimiters (comma, semicolon, dots, newlines, etc.)
function extractKeywords(s: string | undefined): string[] {
  if (!s) return [];
  return s
    .split(/[_,:;.\n\r]+/)
    .map(p => p.trim().toLowerCase())
    .filter(c => c.length >= 2);
}

export function matchGLtoBudget(
  budgetLines: BudgetLine[],
  glRows: GLRow[],
  manualMap: ManualMatchMap,
): MatchedActual[] {
  // 1. Pre-build keyword list for each budget line
  const budgetKeywords = budgetLines.map(line => {
    const key = budgetLineKey(line);
    const keywordsSet = new Set<string>();

    if (line.keterangan) keywordsSet.add(line.keterangan.trim().toLowerCase());
    if (line.keteranganDetail) keywordsSet.add(line.keteranganDetail.trim().toLowerCase());

    extractKeywords(line.keterangan).forEach(kw => keywordsSet.add(kw));
    extractKeywords(line.keteranganDetail).forEach(kw => keywordsSet.add(kw));

    const defaultLine = getDefaultLine(line, budgetLines);
    if (defaultLine) {
      if (defaultLine.keterangan) keywordsSet.add(defaultLine.keterangan.trim().toLowerCase());
      if (defaultLine.keteranganDetail) keywordsSet.add(defaultLine.keteranganDetail.trim().toLowerCase());

      extractKeywords(defaultLine.keterangan).forEach(kw => keywordsSet.add(kw));
      extractKeywords(defaultLine.keteranganDetail).forEach(kw => keywordsSet.add(kw));
    }

    const keywords = Array.from(keywordsSet).filter(kw => kw.length >= 2);
    return { line, key, keywords };
  });

  // 2. Perform de-duplicated matching for each GL Row
  // Maps glRow.rowIndex -> { key: string, isManual: boolean }
  const glToBudgetMap = new Map<number, { key: string; isManual: boolean }>();

  glRows.forEach(r => {
    let bestMatch: { key: string; isManual: boolean; maxScore: number } | null = null;

    for (const item of budgetKeywords) {
      if (item.line.noAcc !== r.accCode) continue;

      // Check if manually matched
      const manualId = String(r.rowIndex);
      if (manualMap[manualId] === item.key) {
        // Manual matches have absolute priority and bypass fuzzy matching
        bestMatch = { key: item.key, isManual: true, maxScore: 99999 };
        break;
      }

      // Check fuzzy matching
      let maxScore = 0;
      const descLower = r.desc.toLowerCase();
      for (const kw of item.keywords) {
        if (descLower.includes(kw)) {
          if (kw.length > maxScore) {
            maxScore = kw.length;
          }
        }
      }

      if (maxScore > 0) {
        if (!bestMatch || (!bestMatch.isManual && maxScore > bestMatch.maxScore)) {
          bestMatch = { key: item.key, isManual: false, maxScore };
        }
      }
    }

    if (bestMatch) {
      glToBudgetMap.set(r.rowIndex, { key: bestMatch.key, isManual: bestMatch.isManual });
    }
  });

  // 3. Assemble MatchedActual results for each budget line
  return budgetLines.map(line => {
    const key = budgetLineKey(line);

    // Filter GL rows allocated to this budget line
    const matchedRows = glRows.filter(r => {
      const match = glToBudgetMap.get(r.rowIndex);
      return match?.key === key;
    });

    const manualMatchedCount = glRows.filter(r => {
      const match = glToBudgetMap.get(r.rowIndex);
      return match?.key === key && match.isManual;
    }).length;

    const actual = matchedRows.reduce((sum, r) => sum + r.amount, 0);
    const hasManual = manualMatchedCount > 0;
    const status: MatchStatus = matchedRows.length === 0
      ? 'unmatched'
      : hasManual ? 'manual' : 'matched';

    return { budgetKey: key, actual, status, matchedRows };
  });
}

